import { Navigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useState, useEffect } from 'react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireRole?: 'job_seeker' | 'employer' | 'admin';
}

export function ProtectedRoute({ children, requireRole }: ProtectedRouteProps) {
  const { user, profile, loading, initialized, fetchProfile } = useAuthStore();
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);

  console.log('[ProtectedRoute] Status:', { initialized, loading, hasUser: !!user, hasProfile: !!profile, profileRole: profile?.role, requireRole, retryCount });

  useEffect(() => {
    if (initialized && user && !profile && !loading && retryCount < 3) {
      const timer = setTimeout(() => {
        console.log('[ProtectedRoute] Auto-retrying profile fetch, attempt:', retryCount + 1);
        setIsRetrying(true);
        fetchProfile().finally(() => {
          setIsRetrying(false);
          setRetryCount(prev => prev + 1);
        });
      }, 1000 * (retryCount + 1));

      return () => clearTimeout(timer);
    }
  }, [initialized, user, profile, loading, retryCount, fetchProfile]);

  // Still initializing
  if (!initialized || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 via-white to-amber-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // No authenticated user
  if (!user) {
    console.log('[ProtectedRoute] No user, redirecting to login');
    return <Navigate to="/login" replace />;
  }

  if (!profile) {
    if (isRetrying || retryCount < 3) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 via-white to-amber-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading your profile...</p>
            {retryCount > 0 && (
              <p className="text-sm text-gray-500 mt-2">Retry attempt {retryCount}/3</p>
            )}
          </div>
        </div>
      );
    }

    console.error('[ProtectedRoute] User exists but profile is null after retries - showing error state');
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 via-white to-amber-50 p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">⚠️</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Profile Not Found</h2>
          <p className="text-gray-600 mb-4">
            Your user account exists but your profile data could not be loaded.
          </p>
          <p className="text-sm text-gray-500 mb-6">
            This may be due to a network issue or database synchronization problem. Please try refreshing or signing out and back in.
          </p>
          <div className="space-y-3">
            <button
              onClick={() => {
                setRetryCount(0);
                setIsRetrying(true);
                fetchProfile().finally(() => setIsRetrying(false));
              }}
              disabled={isRetrying}
              className="w-full px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg font-medium hover:from-orange-600 hover:to-amber-600 transition disabled:opacity-50"
            >
              {isRetrying ? 'Retrying...' : 'Try Again'}
            </button>
            <button
              onClick={() => window.location.reload()}
              className="w-full px-6 py-3 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 transition"
            >
              Reload Page
            </button>
            <button
              onClick={async () => {
                await useAuthStore.getState().signOut();
                window.location.href = '/login';
              }}
              className="w-full px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition"
            >
              Sign Out
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-4">
            If this problem persists, please contact support.
          </p>
        </div>
      </div>
    );
  }

  // Check role requirements
  if (requireRole && profile.role !== requireRole) {
    console.log('[ProtectedRoute] Role mismatch, redirecting to home');
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
